// Generated by tutorials/record.cjs from tutorials/articles/h12-finding-a-resident.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h12 --text-only` (wording) or
// `node tutorials/record.cjs h12` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'finding-a-resident',
  catalogId: 'h12',
  title: 'Finding a resident',
  summary: 'Get to any resident in a few keystrokes from any page, or narrow the Residents list to the people you need, such as one unit or everyone whose weight is due.',
  audience: 'Everyone who looks up residents',
  area: 'residents',
  learn: [
    'Search for a resident from any page with Ctrl and /',
    'Search by name or by unit',
    'Sort and filter the Residents list',
    'Show only residents with a flag, such as a weight that is due',
    'Open a resident\'s record',
  ],
  keywords: [
    'find resident',
    'search',
    'look up',
    'ctrl /',
    'shortcut',
    'resident list',
    'residents',
    'filter',
    'sort',
    'unit',
    'flags',
    'weight flags',
    'open resident',
  ],
  steps: [
    {
      id: 'open-search',
      part: 'Search from any page',
      heading: 'Open the search',
      text: 'On any page, press Ctrl and / together, or select the search box at the top of the screen. The search opens with the cursor already in it.',
      media: {
        image: {
          src: '/help/finding-a-resident/open-search.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/finding-a-resident/open-search.mp4',
          width: 1280,
          height: 748,
          durationSec: 2,
        },
        alt: 'The search window open over the dashboard, with an empty search box at the top.',
      },
    },
    {
      id: 'type-name',
      part: 'Search from any page',
      heading: 'Type part of a name',
      text: 'Type part of a first or last name. Each resident found shows their unit, room and bed, and diet order, so you can be sure you have the right person.',
      media: {
        image: {
          src: '/help/finding-a-resident/type-name.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/finding-a-resident/type-name.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.1,
        },
        alt: 'The search for "rosa" finds Delgado, Rosa, in the Maple unit, room 103A, on a Consistent Carbohydrate, Mechanical Soft, Thin Liquids diet.',
      },
    },
    {
      id: 'type-unit',
      part: 'Search from any page',
      heading: 'Or type a unit',
      text: 'Type the name of a unit to see the residents on it.',
      media: {
        image: {
          src: '/help/finding-a-resident/type-unit.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/finding-a-resident/type-unit.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.1,
        },
        alt: 'The search for "willow" lists residents on the Willow unit with their rooms and diet orders.',
      },
    },
    {
      id: 'open-record',
      part: 'Search from any page',
      heading: 'Open their record',
      text: 'Use the up and down arrows to pick a resident and press Enter, or select them, to open their record.',
      media: {
        image: {
          src: '/help/finding-a-resident/open-record.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/finding-a-resident/open-record.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.6,
        },
        alt: 'The record of a resident on the Willow unit, opened from the search.',
      },
    },
    {
      id: 'open-list',
      part: 'Narrow the Residents list',
      heading: 'Open the Residents list',
      text: 'Select Residents in the menu for everyone at your facility. The line at the bottom counts who is listed: in total, active and discharged.',
      media: {
        image: {
          src: '/help/finding-a-resident/open-list.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/finding-a-resident/open-list.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.2,
        },
        alt: 'The Residents list: unit, room, bed, name, diet order, flags and age for each resident, and the counts at the bottom.',
      },
    },
    {
      id: 'sort',
      part: 'Narrow the Residents list',
      heading: 'Sort the list',
      text: 'Select a column heading to sort by it, such as Rm to go room by room. Select it again to reverse the order.',
      media: {
        image: {
          src: '/help/finding-a-resident/sort.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/finding-a-resident/sort.mp4',
          width: 1280,
          height: 748,
          durationSec: 3,
        },
        alt: 'The Residents list sorted by room number, lowest first.',
      },
    },
    {
      id: 'flag-filter',
      part: 'Narrow the Residents list',
      heading: 'Show residents with a flag',
      text: 'Select Filter and check a flag, such as Weight Flags, to see only the residents whose weight is due or has changed a lot.',
      media: {
        image: {
          src: '/help/finding-a-resident/flag-filter.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/finding-a-resident/flag-filter.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.8,
        },
        alt: 'The Filter list with Weight Flags checked, and the two residents with a Weight flag listed.',
      },
    },
    {
      id: 'clear-filters',
      part: 'Narrow the Residents list',
      heading: 'See everyone again',
      text: 'To see everyone again, select Filter, then Clear Filters.',
      media: {
        image: {
          src: '/help/finding-a-resident/clear-filters.webp',
          width: 1280,
          height: 748,
        },
        alt: 'Clear Filters at the top of the Filter list, marked.',
      },
    },
    {
      id: 'column-filter',
      part: 'Narrow the Residents list',
      heading: 'Show one unit',
      text: 'Select the funnel beside a column heading to choose which values to show. Here, clear Select all, check Birch, then select OK.',
      media: {
        image: {
          src: '/help/finding-a-resident/column-filter.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/finding-a-resident/column-filter.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.9,
        },
        alt: 'The Unit filter list with only Birch checked, above the Residents list.',
      },
    },
    {
      id: 'birch-only',
      part: 'Narrow the Residents list',
      heading: 'The list narrows',
      text: 'Only the residents on Birch are listed. The funnel beside Unit is filled in, so you can see the list is filtered.',
      media: {
        image: {
          src: '/help/finding-a-resident/birch-only.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/finding-a-resident/birch-only.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.6,
        },
        alt: 'The Residents list showing only residents on the Birch unit, with the Unit funnel filled in.',
      },
    },
    {
      id: 'open-from-list',
      part: 'Narrow the Residents list',
      heading: 'Open a resident from the list',
      text: 'Double-click a resident, or select them and then Open, to open their record.',
      media: {
        image: {
          src: '/help/finding-a-resident/open-from-list.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/finding-a-resident/open-from-list.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'The record of Bernard Lindqvist, opened from the Residents list.',
      },
    },
  ],
  tips: [
    'Ctrl and / works on every page, even in the middle of something else. Press Esc to close the search without going anywhere.',
    'Search looks at names and units. To find everyone in one room, use the Residents list and sort by Rm.',
    'A funnel that is filled in means that column is filtered. Select it again to change or clear the filter.',
  ],
  notes: [
    'The Residents list also shows discharged residents. The counts at the bottom say how many are active and how many are discharged.',
  ],
  related: ['getting-around', 'your-dashboard'],
  recorded: '2026-10-25',
};

export default article;
